// Simple i18n utility for landing page
export type Language = 'en' | 'fr' | 'ar';

let currentLanguage: Language = (localStorage.getItem('language') as Language) || 'en';

export const setLanguage = (lang: Language) => {
  currentLanguage = lang;
  localStorage.setItem('language', lang);
  document.dir = lang === 'ar' ? 'rtl' : 'ltr';
};

export const getLanguage = (): Language => currentLanguage;

// Set initial direction
document.dir = currentLanguage === 'ar' ? 'rtl' : 'ltr';

export const translations = {
  en: {
    // Hero Section
    heroTitle: 'souk.immo: The Marketplace for Your Dream House',
    heroSubtitle: 'Match clients with properties intelligently. Manage your real estate business with cutting-edge AI technology.',
    heroCta: 'Get Started',
    heroCtaSecondary: 'Learn More',
    
    // Features
    featuresTitle: 'Powerful Features',
    featuresSubtitle: 'Everything you need to manage your real estate business',
    feature1Title: 'AI Matching',
    feature1Desc: 'Intelligent property-client matching using advanced algorithms',
    feature2Title: 'Client Management',
    feature2Desc: 'Complete CRM for managing client relationships and preferences',
    feature3Title: 'Property Management',
    feature3Desc: 'Comprehensive property listings with advanced search and filters',
    feature4Title: 'Real-time Updates',
    feature4Desc: 'Get instant notifications and updates on matches and activities',
    feature5Title: 'Analytics Dashboard',
    feature5Desc: 'Track performance metrics and gain insights into your business',
    feature6Title: 'Secure & Reliable',
    feature6Desc: 'Built on Firebase with enterprise-grade security and reliability',
    
    // How It Works
    howItWorksTitle: 'How It Works',
    howItWorksSubtitle: 'Get started in three simple steps',
    step1Title: 'Add Properties',
    step1Desc: 'List your properties with detailed information and photos',
    step2Title: 'Add Clients',
    step2Desc: 'Create client profiles with their preferences and requirements',
    step3Title: 'AI Matches',
    step3Desc: 'Our AI automatically finds the best matches and notifies you',
    
    // CTA Section
    ctaTitle: 'Ready to Transform Your Real Estate Business?',
    ctaSubtitle: 'Join thousands of agents using souk.immo to close more deals',
    ctaButton: 'Start Free Trial',
    
    // Footer
    footerTagline: 'souk.immo: The Marketplace for Your Dream House',
    footerRights: 'All rights reserved',
    
    // Navigation
    navFeatures: 'Features',
    navHowItWorks: 'How It Works',
    navPricing: 'Pricing',
    navLogin: 'Login',
    navSignUp: 'Get Started',

    // Tutorial Stepper
    tutorialWelcomeTitle: 'Welcome to souk.immo',
    tutorialWelcomeDesc: 'Your intelligent real estate companion. We help you find your dream property through advanced AI matching.',
    tutorialRolesTitle: 'For Everyone',
    tutorialRolesDesc: 'Whether you are buying your first home, working as a real estate agent, or managing massive development projects, we have tailored experiences for you.',
    tutorialSkip: 'Skip',
    tutorialNext: 'Next',
    tutorialFinish: 'Get Started',
  },
  fr: {
    // Hero Section
    heroTitle: 'souk.immo : La Marketplace pour votre Maison de Rêve',
    heroSubtitle: 'Associez intelligemment les clients aux propriétés. Gérez votre activité immobilière avec une technologie IA de pointe.',
    heroCta: 'Commencer',
    heroCtaSecondary: 'En savoir plus',
    
    // Features
    featuresTitle: 'Fonctionnalités Puissantes',
    featuresSubtitle: 'Tout ce dont vous avez besoin pour gérer votre activité immobilière',
    feature1Title: 'Appariement IA',
    feature1Desc: 'Appariement intelligent propriété-client utilisant des algorithmes avancés',
    feature2Title: 'Gestion des Clients',
    feature2Desc: 'CRM complet pour gérer les relations et préférences des clients',
    feature3Title: 'Gestion des Propriétés',
    feature3Desc: 'Listings de propriétés complets avec recherche et filtres avancés',
    feature4Title: 'Mises à Jour en Temps Réel',
    feature4Desc: 'Recevez des notifications et mises à jour instantanées sur les correspondances et activités',
    feature5Title: 'Tableau de Bord Analytique',
    feature5Desc: 'Suivez les métriques de performance et obtenez des insights sur votre activité',
    feature6Title: 'Sécurisé et Fiable',
    feature6Desc: 'Construit sur Firebase avec une sécurité et une fiabilité de niveau entreprise',
    
    // How It Works
    howItWorksTitle: 'Comment Ça Marche',
    howItWorksSubtitle: 'Commencez en trois étapes simples',
    step1Title: 'Ajouter des Propriétés',
    step1Desc: 'Listez vos propriétés avec des informations détaillées et des photos',
    step2Title: 'Ajouter des Clients',
    step2Desc: 'Créez des profils clients avec leurs préférences et exigences',
    step3Title: 'Appariements IA',
    step3Desc: 'Notre IA trouve automatiquement les meilleures correspondances et vous notifie',
    
    // CTA Section
    ctaTitle: 'Prêt à Transformer Votre Activité Immobilière?',
    ctaSubtitle: "Rejoignez des milliers d'agents utilisant souk.immo pour conclure plus d'affaires",
    ctaButton: "Commencer l'Essai Gratuit",
    
    // Footer
    footerTagline: 'souk.immo : La Marketplace pour votre Maison de Rêve',
    footerRights: 'Tous droits réservés',
    
    // Navigation
    navFeatures: 'Fonctionnalités',
    navHowItWorks: 'Comment Ça Marche',
    navPricing: 'Tarifs',
    navLogin: 'Connexion',
    navSignUp: 'Commencer',

    // Tutorial Stepper
    tutorialWelcomeTitle: 'Bienvenue sur souk.immo',
    tutorialWelcomeDesc: 'Votre compagnon immobilier intelligent. Nous vous aidons à trouver la propriété de vos rêves grâce à l\'appariement avancé par IA.',
    tutorialRolesTitle: 'Pour Tout le Monde',
    tutorialRolesDesc: 'Que vous achetiez votre première maison, que vous travailliez comme agent immobilier ou que vous gériez des projets de développement massifs, nous avons des expériences sur mesure pour vous.',
    tutorialSkip: 'Passer',
    tutorialNext: 'Suivant',
    tutorialFinish: 'Commencer',
  },
  ar: {
    // Hero Section
    heroTitle: 'souk.immo: منصة بيت أحلامك',
    heroSubtitle: 'اربط العملاء بالعقارات بذكاء. أدر عملك العقاري باستخدام أحدث تقنيات الذكاء الاصطناعي.',
    heroCta: 'ابدأ الآن',
    heroCtaSecondary: 'تعرف على المزيد',
    
    // Features
    featuresTitle: 'مميزات قوية',
    featuresSubtitle: 'كل ما تحتاجه لإدارة عملك العقاري',
    feature1Title: 'طابق ذكي',
    feature1Desc: 'مطابقة ذكية بين العقارات والعملاء باستخدام خوارزميات متقدمة',
    feature2Title: 'إدارة العملاء',
    feature2Desc: 'نظام متكامل لإدارة علاقات العملاء وتفضيلاتهم',
    feature3Title: 'إدارة العقارات',
    feature3Desc: 'قوائم عقارات شاملة مع بحث وفلاتر متقدمة',
    feature4Title: 'تحديثات مباشرة',
    feature4Desc: 'احصل على إشعارات وتحديثات فورية حول المطابقات والأنشطة',
    feature5Title: 'لوحة تحكم تحليلية',
    feature5Desc: 'تتبع مقاييس الأداء واحصل على رؤى حول عملك',
    feature6Title: 'آمن وموثوق',
    feature6Desc: 'مبني على Firebase مع أمان وموثوقية بمستوى المؤسسات',
    
    // How It Works
    howItWorksTitle: 'كيف يعمل؟',
    howItWorksSubtitle: 'ابدأ في ثلاث خطوات بسيطة',
    step1Title: 'أضف العقارات',
    step1Desc: 'أدرج عقاراتك بمعلومات مفصلة وصور',
    step2Title: 'أضف العملاء',
    step2Desc: 'أنشئ ملفات تعريف للعملاء مع تفضيلاتهم ومتطلباتهم',
    step3Title: 'مطابقات الذكاء الاصطناعي',
    step3Desc: 'يقوم نظامنا تلقائيًا بالعثور على أفضل المطابقات وإخطارك',
    
    // CTA Section
    ctaTitle: 'هل أنت مستعد لتطوير عملك العقاري؟',
    ctaSubtitle: 'انضم إلى آلاف الوكلاء الذين يستخدمون souk.immo لإتمام المزيد من الصفقات',
    ctaButton: 'ابدأ التجربة المجانية',
    
    // Footer
    footerTagline: 'نظام إدارة عقارات مدعوم بالذكاء الاصطناعي',
    footerRights: 'جميع الحقوق محفوظة',
    
    // Navigation
    navFeatures: 'المميزات',
    navHowItWorks: 'كيف يعمل',
    navPricing: 'الأسعار',
    navLogin: 'تسجيل الدخول',
    navSignUp: 'ابدأ الآن',

    // Tutorial Stepper
    tutorialWelcomeTitle: 'مرحبًا بك في souk.immo',
    tutorialWelcomeDesc: 'رفيقك العقاري الذكي. نساعدك في العثور على عقار أحلامك من خلال المطابقة المتقدمة بالذكاء الاصطناعي.',
    tutorialRolesTitle: 'للجميع',
    tutorialRolesDesc: 'سواء كنت تشتري منزلك الأول، أو تعمل كوكيل عقاري، أو تدير مشاريع تطوير ضخمة، لدينا تجارب مصممة خصيصًا لك.',
    tutorialSkip: 'تخطي',
    tutorialNext: 'التالي',
    tutorialFinish: 'أبدأ الآن',
  },
};

export const t = (key: keyof typeof translations.en): string => {
  return translations[currentLanguage][key] || translations.en[key];
};

